import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import { useMutation } from "@apollo/client";
import { startCase } from "lodash";
import { GET_MISSION_ROLES } from "graphql/queries";
import {
  ADD_MISSION_ROLE,
  APPROVE_MISSION_ROLE,
  CLOSE_MISSION_ROLE,
  PUBLISH_MISSION_ROLE,
  REMOVE_MISSION_ROLES,
  REOPEN_MISSION_ROLE,
} from "graphql/mutations";
import { MODAL_SIZE } from "components/Modal";
import { PAGINATION_TYPE } from "components/Pagination";
import { useAuth, useLinks, useModalConfirmation, useQueryData, useToast } from "hooks";
import Icon, { ICON_TYPE } from "components/Icon";
import { ARCHETYPES_OPTIONS, isDraftInProgress, MISSION_ROLE_STATES } from "constants/index";
import { Col, Row } from "components/Containers";
import { colors } from "style";
import { getGraphqlDetailedErrors } from "utils";

/**
 * useMissionRoles
 *
 * @param {String}  missionId
 * @param {Object}  filters
 * @param {Object}  paginationType
 * @param {Number}  resultsPerPage
 * @param {Boolean} skipQuery
 * @param {Array}   archetypeOptions  Labels the restored archetype with the list the form offers
 */
export default function useMissionRoles({
  ids,
  missionId,
  filters = {},
  orderBy = {},
  paginationType = PAGINATION_TYPE.classic,
  resultsPerPage,
  skipQuery = false,
  disablePagination = false,
  includeProfilesCount = false,
  includeCustomerBudget = false,
  archetypeOptions = ARCHETYPES_OPTIONS,
}) {
  const { toast } = useToast();
  const { isTalentSimpleClient } = useAuth();
  const { show } = useModalConfirmation();
  const links = useLinks();
  const navigate = useNavigate();
  const variables = missionId ? { id: missionId } : {};
  const [showForm, setShowForm] = useState(false);
  const [showReorganizeForm, setShowReorganizeForm] = useState(false);
  const [showPublicViewForm, setShowPublicViewForm] = useState(false);
  const [selectedRole, setSelectedRole] = useState({});
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishRole, setPublishRole] = useState(null);
  const [shouldNotifySubscribers, setShouldNotifySubscribers] = useState(false);

  const { loading, loadingMore, data, error, hasNextPage, handleNextPage, handlePageChange } = useQueryData({
    queryName: GET_MISSION_ROLES,
    keyName: "missionRoles",
    resultsPerPage,
    paginationType,
    skip: skipQuery,
    variables: {
      ...variables,
      filters: { ids, ...filters },
      orderBy,
      disablePagination,
      includeProfilesCount,
      includeCustomerBudget,
    },
    disablePagination,
  });

  const [removeMissionRoles, { loading: deleting }] = useMutation(REMOVE_MISSION_ROLES, {
    refetchQueries: [GET_MISSION_ROLES],
  });
  const [closeMissionRole, { loading: closing }] = useMutation(CLOSE_MISSION_ROLE, {
    refetchQueries: [GET_MISSION_ROLES],
  });
  const [reopenMissionRole, { loading: reopening }] = useMutation(REOPEN_MISSION_ROLE, {
    refetchQueries: [GET_MISSION_ROLES],
  });
  const [approveMissionRole, { loading: approving }] = useMutation(APPROVE_MISSION_ROLE, {
    refetchQueries: [GET_MISSION_ROLES],
  });
  const [publishMissionRole, { loading: publishing }] = useMutation(PUBLISH_MISSION_ROLE, {
    refetchQueries: [GET_MISSION_ROLES],
  });
  const [duplicateMissionRole, { loading: duplicating }] = useMutation(ADD_MISSION_ROLE, {
    refetchQueries: [GET_MISSION_ROLES],
  });

  const buildFormData = (
    missionRole,
    setUnpublished = false,
    isInProgress = false,
    builderStep = missionRole?.builderStep
  ) => {
    const { archetype } = missionRole || {};
    const missionRoleArchetypes = typeof archetype === "object" ? archetype : archetype?.split(",");
    const state = setUnpublished ? MISSION_ROLE_STATES.unpublished : missionRole?.state;
    // To get rid of typename key in the customer budget object
    const { rate, currency, frequency } = missionRole?.customerBudget || {};
    const { min, max } = rate || {};

    return {
      name: missionRole?.name,
      description: missionRole?.description,
      descriptionJson: missionRole?.descriptionJson,
      archetype: archetypeOptions.filter((item) => missionRoleArchetypes?.some((id) => item.id === id)) || [],
      primarySkills: missionRole?.primarySkills?.map(({ name, id }) => ({ name, id })),
      secondarySkills: missionRole?.secondarySkills?.map(({ name, id }) => ({ name, id })),
      disciplines: missionRole?.disciplines?.map(({ name, id }) => ({ name, id })),
      startDate: missionRole?.startDate,
      engagementType: missionRole?.engagementType,
      engagementTime: missionRole?.engagementTime,
      targetRate: {
        amount: missionRole?.targetRate,
        currency: missionRole?.targetRateCurrency,
      },
      timezone: missionRole?.timezone?.value,
      isVisible: state === MISSION_ROLE_STATES.published,
      state,
      assignedToId: missionRole?.assignedTo?.id,
      talentOwnerId: missionRole?.talentOwner?.id,
      remoteAnywhere: missionRole?.id ? missionRole?.remoteAnywhere : true,
      workArrangement: missionRole?.workArrangement,
      location: missionRole?.location,
      locations: missionRole?.locations?.map(({ name, id }) => ({ name, id })),
      customerBudget: { rate: { min, max }, currency, frequency },
      missionId,
      isInProgress,
      ...(isInProgress ? { builderStep } : {}),
    };
  };

  const handleRemoveRoles = async (ids, isBatch, role) => {
    const response = await show(
      <FormattedMessage id={`Missions.Roles.RoleAdvancedOptions.Remove.Title${isBatch ? `Batch` : ``}`} />,
      <Col gap="1rem">
        <span>
          <FormattedMessage
            id={`Missions.Roles.RoleAdvancedOptions.Remove.Confirmation${isBatch ? `` : `1`}`}
            values={{
              name: <b>{role?.name}</b>,
            }}
          />
        </span>
        <b>
          <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Remove.Confirmation2" />
        </b>
      </Col>,
      {
        showCancel: true,
        showSave: false,
        showSaveAsDestructable: true,
        primaryButtonMessageId: `Missions.Roles.RoleAdvancedOptions.Remove.Cta${isBatch ? `Batch` : ``}`,
      }
    );

    if (response) {
      return removeMissionRoles({
        variables: {
          input: { ids },
        },
        onCompleted: () =>
          toast.success(
            <FormattedMessage
              id={"useMissionRoles.RemoveSuccess"}
              values={{
                roleCount: ids.length,
              }}
            />
          ),
        onError: () =>
          toast.error(
            <FormattedMessage
              id={"useMissionRoles.RemoveError"}
              values={{
                roleCount: ids.length,
              }}
            />
          ),
      });
    }
  };

  /**
   * Triggers the close mission role query
   */
  const handleCloseRole = async (role) => {
    const response = await show(
      <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Close.Title" />,
      <Col gap="1rem">
        <span>
          <FormattedMessage
            id="Missions.Roles.RoleAdvancedOptions.Close.Confirmation1"
            values={{
              name: <b>{role?.name}</b>,
            }}
          />
        </span>
        <b>
          <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Close.Confirmation2" />
        </b>
      </Col>,
      {
        showCancel: true,
        showSave: false,
        showSaveAsDestructable: true,
        primaryButtonMessageId: "Missions.Roles.RoleAdvancedOptions.Close.Cta",
      }
    );

    if (response) {
      await closeMissionRole({
        variables: {
          input: { id: role?.id },
        },
        onCompleted: () => toast.success(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Close.Success" />),
        onError: () => toast.error(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Close.Error" />),
      });
    }
  };

  /**
   * Triggers the approve mission role query
   */
  const handleApproveRole = async (role) =>
    await approveMissionRole({
      variables: {
        input: { id: role?.id },
      },
      onCompleted: () => toast.success(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Approve.Success" />),
      onError: () => toast.error(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Approve.Error" />),
    });

  /**
   * Triggers the reopen mission role mutation
   */
  const handleReopenRole = async (role) => {
    const response = await show(
      <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Reopen.Title" />,
      <Col gap="1rem">
        <span>
          <FormattedMessage
            id="Missions.Roles.RoleAdvancedOptions.Reopen.Confirmation1"
            values={{
              name: <b>{role?.name}</b>,
            }}
          />
        </span>
        <b>
          <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Reopen.Confirmation2" />
        </b>
      </Col>,
      {
        showCancel: true,
        primaryButtonMessageId: "Missions.Roles.RoleAdvancedOptions.Reopen.Cta",
      }
    );

    if (response) {
      await reopenMissionRole({
        variables: {
          input: { id: role?.id },
        },
        onCompleted: () => toast.success(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Reopen.Success" />),
        onError: () => toast.error(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Reopen.Error" />),
      });
    }
  };

  /**
   * Triggers the duplicate mission role query
   */
  const handleDuplicateRole = async (missionRole) => {
    const { mission } = missionRole;

    const response = await show(
      <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Duplicate.Title" />,
      <Col gap="1rem">
        <span>
          <FormattedMessage
            id="Missions.Roles.RoleAdvancedOptions.Duplicate.Confirmation1"
            values={{
              name: <b>{missionRole?.name}</b>,
            }}
          />
        </span>
        <b>
          <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Duplicate.Confirmation2" />
        </b>
      </Col>,
      {
        showCancel: true,
        showSave: true,
        primaryButtonMessageId: "Missions.Roles.RoleAdvancedOptions.Duplicate.Cta",
      }
    );

    if (response) {
      await duplicateMissionRole({
        variables: {
          input: {
            missionId: mission?.uuid,
            params: {
              ...buildFormData(missionRole, true),
              name: `[DUPLICATE] ${missionRole?.name}`,
              copiedRoleId: missionRole?.slug,
              isVisible: false,
            },
          },
        },
        onCompleted: async ({ addMissionRole }) => {
          navigate(links.mission_role_details(mission?.uuid, addMissionRole?.missionRole?.slug));
          toast.success(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Duplicate.Success" />);
        },
        onError: () => toast.error(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Duplicate.Error" />),
      });
    }
  };

  const handleReorganizeRole = async (role) => {
    setSelectedRole(role);
    setShowReorganizeForm(true);
  };

  const showDetailedError = async (errors, titleId, descriptionId) =>
    await show(
      <FormattedMessage id={titleId} />,
      <>
        <p>
          <FormattedMessage id={descriptionId} />
        </p>
        <p>
          <b>
            <FormattedMessage id="Missions.MissionPublishButton.DetailedErrorDescriptionIssuesCopy" />
          </b>
        </p>
        <Col gap="0.5rem">
          {getGraphqlDetailedErrors(errors)?.map((detailedError, index) => (
            <Row key={index} noWrap>
              <Icon type={ICON_TYPE.unavailable} color={colors.red} />
              <span>
                {typeof detailedError === "string"
                  ? detailedError
                  : `${startCase(detailedError?.field)} ${detailedError?.message}`}
              </span>
            </Row>
          ))}
        </Col>
      </>,
      {
        showCancel: true,
        showSave: false,
        secondaryButtonMessageId: "Global.Close",
        size: MODAL_SIZE.small,
      }
    );

  const handlePublishRole = async (role) => {
    setPublishRole(role);
    setShouldNotifySubscribers(false);
    setShowPublishModal(true);
  };

  const handleUnpublishRole = async (role) => {
    const response = await show(
      <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Unpublish.Title" />,
      <Col gap="1rem">
        <span>
          <FormattedMessage
            id="Missions.Roles.RoleAdvancedOptions.Unpublish.Confirmation1"
            values={{ name: <b>{role?.name}</b> }}
          />
        </span>
        <b>
          <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Publish.Confirmation2" />
        </b>
      </Col>,
      {
        showCancel: true,
        primaryButtonMessageId: "Missions.Roles.RoleAdvancedOptions.Unpublish.Cta",
      }
    );

    if (!response) return;

    await publishMissionRole({
      variables: { input: { id: role?.id, publish: false } },
      onCompleted: () => toast.success(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Unpublish.Success" />),
      onError: () => toast.error(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Unpublish.Error" />),
    });
  };

  const handleConfirmPublishRole = async () => {
    if (!publishRole) return;

    setShowPublishModal(false);

    await publishMissionRole({
      variables: {
        input: {
          id: publishRole?.id,
          publish: true,
          shouldNotifySubscribers,
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Publish.Success" />),
      onError: async (errors) => {
        getGraphqlDetailedErrors(errors)?.length
          ? await showDetailedError(
              errors,
              "Missions.Roles.RoleAdvancedOptions.Publish.ErrorTitle",
              "Missions.Roles.RoleAdvancedOptions.Publish.ErrorDescription"
            )
          : toast.error(<FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Publish.Error" />);
      },
    });
  };

  const getAdvancedOptions = (data, selection) => {
    const editOption = {
      label: <FormattedMessage id={`Missions.Roles.RoleAdvancedOptions.Edit`} />,
      icon: ICON_TYPE.edit,
      onClick: () => {
        setSelectedRole(data);
        setShowForm(true);
      },
    };

    const removeOption = {
      label: (
        <FormattedMessage
          id={`Missions.Roles.RoleAdvancedOptions.${data ? `Remove` : `BatchRemove`}`}
          values={{
            roleCount: selection?.length || 0,
          }}
        />
      ),
      icon: ICON_TYPE.trash,
      onClick: () => handleRemoveRoles(data ? [data?.id] : selection, data ? false : true, data),
    };

    const copyOption = {
      label: <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Duplicate" />,
      icon: ICON_TYPE.copy,
      onClick: () => handleDuplicateRole(data),
    };

    const closeOption = {
      label: <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Close" />,
      icon: ICON_TYPE.attention,
      onClick: () => handleCloseRole(data),
    };

    const approveOption = {
      label: <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Approve" />,
      icon: ICON_TYPE.checkmark,
      onClick: () => handleApproveRole(data),
    };

    const reopenOption = {
      label: <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Reopen" />,
      icon: ICON_TYPE.refresh,
      onClick: () => handleReopenRole(data),
    };

    const publishOption = {
      label: <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Publish" />,
      icon: ICON_TYPE.checkDotted,
      onClick: () => handlePublishRole(data),
    };

    const unpublishOption = {
      label: <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Unpublish" />,
      icon: ICON_TYPE.unavailable,
      onClick: () => handleUnpublishRole(data, false),
    };

    const configurePublicViewOption = {
      label: <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.ConfigurePublicView" />,
      icon: ICON_TYPE.gear,
      onClick: () => {
        setSelectedRole(data);
        setShowPublicViewForm(true);
      },
    };

    const reorganizeOption = {
      label: <FormattedMessage id="Missions.Roles.RoleAdvancedOptions.Reorganize" />,
      icon: ICON_TYPE.refresh,
      onClick: () => handleReorganizeRole(data),
    };

    if (isTalentSimpleClient) {
      const state = data?.state;

      if ([MISSION_ROLE_STATES.unpublished, MISSION_ROLE_STATES.unpublishedNew].includes(state)) {
        return [editOption, publishOption];
      }
      if (state === MISSION_ROLE_STATES.published) return [unpublishOption];

      return null;
    }

    const options = [];

    if (data) {
      const { state } = data;

      if (!isDraftInProgress(state)) {
        options.push(editOption);
        options.push(copyOption);
      }

      if (state !== MISSION_ROLE_STATES.published) {
        options.push(removeOption);
      }

      options.push(reorganizeOption);
      options.push(configurePublicViewOption);

      if (![MISSION_ROLE_STATES.closed, MISSION_ROLE_STATES.draft].includes(state)) {
        options.push(closeOption);
      }

      if (state === MISSION_ROLE_STATES.closed) {
        options.push(reopenOption);
      }

      if (state === MISSION_ROLE_STATES.draft) {
        options.push(approveOption);
      }

      if ([MISSION_ROLE_STATES.unpublishedNew, MISSION_ROLE_STATES.unpublished].includes(state)) {
        options.push(publishOption);
      }

      if (state === MISSION_ROLE_STATES.published) {
        options.push(unpublishOption);
      }
    } else {
      options.push(removeOption);
    }

    return options;
  };

  return {
    loading,
    loadingMore,
    deleting,
    closing,
    reopening,
    duplicating,
    approving,
    publishing,
    handleRemoveRoles,
    getAdvancedOptions,
    showPublishModal,
    setShowPublishModal,
    publishRole,
    shouldNotifySubscribers,
    setShouldNotifySubscribers,
    handleConfirmPublishRole,
    showForm,
    setShowForm,
    showReorganizeForm,
    setShowReorganizeForm,
    showPublicViewForm,
    setShowPublicViewForm,
    buildFormData,
    selectedRole,
    data: data?.missionRoles?.nodes,
    error,
    hasNextPage,
    handleNextPage,
    handlePageChange,
    pageInfo: data?.missionRoles?.pageInfo,
    totalCount: data?.missionRoles?.totalCount,
    currentPage: data?.missionRoles?.currentPage,
    resultsPerPage: data?.missionRoles?.perPage,
  };
}
